import api from './api';

export const authService = {
  // ===== AUTHENTICATION =====
  
  async login(username, password) {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  async register(userData) {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } finally {
      // Always clear local storage even if API call fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
    return { success: true };
  },

  // ===== PROFILE MANAGEMENT =====
  
  async getProfile() {
    const response = await api.get('/auth/profile');
    return response.data.user;
  },

  async updateProfile(userData) {
    const response = await api.put('/auth/profile', userData);
    return response.data;
  },

  async changePassword(currentPassword, newPassword) {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  // ===== TOKEN MANAGEMENT =====
  
  async refreshToken() {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) throw new Error('No refresh token');

    const response = await api.post('/auth/refresh', {
      refresh_token,
    });
    
    const { access_token } = response.data;
    localStorage.setItem('access_token', access_token);
    return access_token;
  },

  // ===== EMAIL VERIFICATION =====
  
  async verifyEmail(token) {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  async resendVerification(email) {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  // ===== PASSWORD RESET =====
  
  /**
   * Request a password reset email
   * @param {string} email - User's email address
   */
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Validate if a reset token is still valid
   * @param {string} token - Reset token from URL
   */
  async validateResetToken(token) {
    const response = await api.get(`/auth/validate-reset-token/${token}`);
    return response.data;
  },

  /**
   * Reset password using token
   * @param {string} token - Reset token from URL
   * @param {string} newPassword - New password
   */
  async resetPassword(token, newPassword) {
    const response = await api.post(`/auth/reset-password/${token}`, {
      new_password: newPassword
    });
    return response.data;
  },

  // ===== HELPER METHODS =====
  
  /**
   * Check if user is authenticated (has valid token)
   */
  isAuthenticated() {
    const token = localStorage.getItem('access_token');
    if (!token) return false;
    
    try {
      // Check if token is expired
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() < exp;
    } catch {
      return false;
    }
  },

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Store user data after login
   * @param {Object} data - Login response data
   */
  setSession(data) {
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  },

  /**
   * Clear all session data
   */
  clearSession() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }
};